import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { ODataArray, ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NotificationService } from '../../services/notification/notification.service';
import { TimesheetAndExpensesService } from '../../services/timesheet/timesheet-and-expenses.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { ArrayModel, JsonArrayModel } from '../complex/array.model';
import { CompositeModel, ODataCompositeOutModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { ExpenseModel, ExpenseModelFromJson, JsonExpenseModel } from '../low-level/expense.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../low-level/notification-identifier.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { TextModelOptions } from '../options/text-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { TextModel } from '../primitive/text.model';
import { UserMasterService } from '../../services/user-master/user-master.service';

export class ConfirmationExpensesModel extends CompositeModel {
    public distance: TextModel;
    public expenses: ArrayModel<ExpenseModel>;
    public mileageTravelStart: DropdownModel<TextModel>;
    public mileageTravelEnd: string;
    public additionalTravelExpenses: DisplayDecorator<BooleanModel>;
    public displayDistance: boolean;
    public displayExpenses: boolean;
    public displayMileageTravel: boolean;
    public displayAdditionalTravelCost: boolean;
    public constructor(
        public notificationId: NotificationIdentifierModel,
        public isRemoteWork = false,
        distance?: string,
        public isDistanceReadonly?: boolean,
    ) {
        super('ConfirmationExpensesModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get<TranslateService>(TranslateService);
        const modelOptionsLoader = injector.get<ModelOptionsLoaderService>(ModelOptionsLoaderService);
        const configService = injector.get<ConfigService>(ConfigService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        const userMasterService = injector.get<UserMasterService>(UserMasterService);

        this.displayDistance =
            clientManager.isNgmSA() || (configService.getDisplayConfirmationDistance() && !this.isRemoteWork);
        this.displayExpenses =
            !userMasterService.isPaceOnlineUser() &&
            !clientManager.isNgmSA() &&
            !this.isRemoteWork &&
            !configService.isTimesheetDisabled() &&
            configService.getDisplayConfirmationExpenses();
        this.displayMileageTravel = configService.getUseMileageHistory();
        this.displayAdditionalTravelCost = configService.getDisplayAdditionalTravelCost() && !this.isRemoteWork;

        if (this.displayMileageTravel) {
            const valueHelperService = injector.get<ValueHelperService>(ValueHelperService);
            const notificationService = injector.get<NotificationService>(NotificationService);
            const timesheetAndExpenseService = injector.get<TimesheetAndExpensesService>(TimesheetAndExpensesService);
            const notificationModel = notificationService.getNotificationByIdentifier(notificationId);
            const vhTravelStartEntries = valueHelperService
                .getTravelOfficeStart()
                .map(
                    (vh: ValueHelperModel) =>
                        new DisplayDecorator<TextModel>(vh.descText, new TextModel('TravelStartPlace', vh.descText)),
                );
            let notificationAddresses: Array<DisplayDecorator<TextModel>> = [];
            if (notificationModel) {
                notificationAddresses = timesheetAndExpenseService
                    .getAddresses()
                    .filter((address) => address.completeName !== notificationModel.address.completeName)
                    .map(
                        (address) =>
                            new DisplayDecorator<TextModel>(
                                address.completeName,
                                new TextModel('TravelStartPlace', address.completeName),
                            ),
                    );
            }
            this.mileageTravelStart = new DropdownModel<TextModel>('TravelStartPlace', [
                ...notificationAddresses,
                ...vhTravelStartEntries,
            ]);
            modelOptionsLoader.load(this.mileageTravelStart, 'ConfirmationExpense', 'TravelStartPlace');
            this.mileageTravelEnd = notificationModel ? notificationModel.address.completeName : '';
        }

        if (this.displayDistance) {
            this.distance = <TextModel>this.set(
                new TextModel(
                    'KiloMeter',
                    distance,
                    new TextModelOptions({
                        unit: <string>translate.instant('CONFIRMATION_EXPENSES_KM_LABEL'),
                        inputType: ConfigConstants.inputTypeNumeric,
                    }),
                ),
            );
            modelOptionsLoader.load(this.distance, 'ConfirmationExpense', 'Distance');
            if (typeof isDistanceReadonly !== 'undefined') {
                this.distance = assignModelOptions(
                    this.distance,
                    {
                        ...this.distance.options,
                        readonly: isDistanceReadonly,
                    },
                    this,
                );
            }
        }
        if (this.displayExpenses) {
            this.expenses = new ArrayModel<ExpenseModel>('CseExpenseSet');
        }
        if (this.displayAdditionalTravelCost) {
            const additionalTravelExpenses = new BooleanModel('AdditionalTravelExpenses');
            this.additionalTravelExpenses = new DisplayDecorator<BooleanModel>('', additionalTravelExpenses);
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): JsonConfirmationExpensesModel {
        return {
            notificationId: this.notificationId,
            distance: this.distance,
            expenses: this.expenses,
            mileageTravelStart:
                this.mileageTravelStart && this.mileageTravelStart.selectedEntry
                    ? this.mileageTravelStart.selectedEntry.value
                    : undefined,
            additionalTravelExpenses: this.displayAdditionalTravelCost
                ? this.additionalTravelExpenses?.model.value
                : false,
        };
    }

    public getOutModel(): ODataCompositeOutModel {
        const outModel = <ODataConfirmationExpensesOutModel>super.getOutModel();

        if (this.additionalTravelExpenses?.model.value) {
            outModel.CrmLongTextTRIDSet = [
                {
                    TaskNo: this.notificationId.maNum,
                    Notification: this.notificationId.qmNum,
                    TextID: 'TRID',
                    TextLine: 'X',
                },
            ];
        }
        return outModel;
    }
}

export class ConfirmationExpensesModelFromJson extends ConfirmationExpensesModel {
    public constructor(data: JsonConfirmationExpensesModel, isNotificationRemoteWork: boolean) {
        super(
            new NotificationIdentifierModelFromJson(data.notificationId),
            isNotificationRemoteWork,
            data.distance ? data.distance.value : undefined,
            data.distance?.options.readonly,
        );
        if (this.displayExpenses) {
            const expenses = data.expenses.value.map((expense: JsonExpenseModel) => {
                return new ExpenseModelFromJson(expense);
            });
            this.expenses.addModels(expenses);
        }
        if (this.mileageTravelStart) {
            this.mileageTravelStart.selectByPredicate((mileage) => mileage.value === data.mileageTravelStart);
        }
        if (this.displayAdditionalTravelCost) {
            this.additionalTravelExpenses.model.value = data.additionalTravelExpenses;
        }
    }
}

export interface JsonConfirmationExpensesModel {
    notificationId: JsonNotificationIdentifierModel;
    distance: TextModel;
    expenses: JsonArrayModel;
    mileageTravelStart?: string;
    additionalTravelExpenses: boolean;
}

export interface ODataConfirmationExpensesOutModel extends ODataObject {
    KiloMeter: string;
    CseExpenseSet: ODataArray;
    CrmLongTextTRIDSet: ODataAdditionalInfoExpensesOutModel[];
}

export interface ODataAdditionalInfoExpensesOutModel extends ODataObject {
    Notification: string;
    TaskNo: string;
    TextID: string;
    TextLine: string;
}
